import React, { useState, useEffect } from 'react';
import { Check, Loader2, AlertCircle, RefreshCw, Link as LinkIcon } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { publicAPI, getErrorMessage } from '../lib/api';
import PaymentCheckoutModal from '../components/PaymentCheckoutModal';
import SampleReportSection from '../components/SampleReportSection';
import climbVideo from '../assets/videos/climb.mp4';

const Pricing = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('stock_recommendations');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [initialCoupon, setInitialCoupon] = useState('');
  const [couponTargetPlanId, setCouponTargetPlanId] = useState(null);

  // Read tab and coupon from URL params on mount
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const couponParam = searchParams.get('coupon');
    const planIdParam = searchParams.get('planId');
    
    // Set active tab from URL parameter
    if (tabParam === 'stock_recommendations' || tabParam === 'live_corporate_announcements') {
      setActiveTab(tabParam);
    }
    
    // Store coupon from URL for auto-apply in checkout modal
    if (couponParam) {
      setInitialCoupon(couponParam);
    }
    
    // Store the target plan ID the coupon is for (spinner coupons are plan-specific)
    if (planIdParam) {
      setCouponTargetPlanId(Number(planIdParam));
    }
  }, [searchParams]);

  // Handle Subscribe Now click
  const handleSubscribeClick = (plan) => {
    setSelectedPlan(plan);
    setIsCheckoutOpen(true);
  };

  // Close checkout modal
  const handleCloseCheckout = () => {
    setIsCheckoutOpen(false);
    setSelectedPlan(null);
  };

  // Fetch pricing plans from API
  const fetchPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await publicAPI.getPricingPlans();
      const data = response.data?.data || response.data || [];
      setPlans(data);
    } catch (err) {
      console.error('Failed to fetch pricing plans:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Filter plans by category
  const stockPlans = plans.filter(p => p.plan_category === 'stock_recommendations');
  const icaPlans = plans.filter(p => p.plan_category === 'live_corporate_announcements');
  const currentPlans = activeTab === 'stock_recommendations' ? stockPlans : icaPlans;

  // Find the most popular plan (middle one or highest savings)
  const getMostPopularPlanId = (plansList) => {
    if (plansList.length === 0) return null;
    if (plansList.length === 3) return plansList[1].id; // Middle plan
    // Otherwise, pick the one with highest savings percentage
    const sorted = [...plansList].sort((a, b) => (b.savings_percentage || 0) - (a.savings_percentage || 0));
    return sorted[0]?.id;
  };

  const mostPopularId = getMostPopularPlanId(currentPlans);

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  // Render plan card
  const renderPlanCard = (plan, isPopular) => (
    <div
      key={plan.id}
      className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-2 ${
        isPopular ? 'ring-4 ring-yellow-400 relative' : ''
      }`}
    >
      {isPopular && (
        <div className="bg-yellow-400 text-gray-900 text-center py-2 font-bold text-sm">
          MOST POPULAR
        </div>
      )}
      <div className="p-8">
        <h3 className="text-3xl font-bold mb-4 text-gray-900">{plan.plan_name}</h3>
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">Duration: {plan.duration_months} Months</p>
          {/* Show strikethrough fees only if there's a discount */}
          {plan.after_discount && plan.after_discount < plan.fees && (
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-gray-400 line-through">Fees: {formatCurrency(plan.fees)}</span>
            </div>
          )}
          <div className="flex items-baseline gap-2 mt-2">
            {/* Show discounted price if available, otherwise show fees as main price */}
            <span className="text-4xl font-bold text-gray-900">
              {formatCurrency(plan.after_discount || plan.fees)}
            </span>
            {/* Only show "After Discount" text if there's actually a discount */}
            {plan.after_discount && plan.after_discount < plan.fees && (
              <span className="text-sm text-green-600 font-semibold">After Discount</span>
            )}
          </div>
          {plan.savings_percentage > 0 && (
            <p className="text-xs text-green-600 mt-1">Save {plan.savings_percentage}%</p>
          )}
        </div>
        <ul className="space-y-3 mb-8">
          {(plan.features || []).map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check className="text-green-500 flex-shrink-0 mt-1" size={20} />
              <span className="text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>
        <Link
          to={`/plans/${plan.id}`}
          className={`block w-full text-center py-3 rounded-lg font-bold transition-colors ${
            isPopular
              ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-500'
              : 'bg-[#1b2d4a] text-white hover:bg-[#2a4a6f]'
          }`}
        >
          Subscribe Now
        </Link>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
          <p className="text-gray-600">Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-3 text-red-500" size={48} />
          <p className="text-red-600 font-semibold mb-2">Failed to load pricing plans</p>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <button
            onClick={fetchPlans}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={18} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-6">
          <h1 className="text-5xl font-bold text-center mb-4 text-gray-900">
            PRICING PLAN
          </h1>
          <p className="text-xl text-center text-gray-600 mb-8">
            Research Based Stock Recommendations
          </p>

          {/* Tabs */}
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={() => setActiveTab('stock_recommendations')}
              className={`px-8 py-3 rounded-full font-semibold transition-all ${
                activeTab === 'stock_recommendations'
                  ? 'bg-yellow-400 text-gray-900'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              Stock Recommendations
            </button>
            <button
              onClick={() => setActiveTab('live_corporate_announcements')}
              className={`px-8 py-3 rounded-full font-semibold transition-all ${
                activeTab === 'live_corporate_announcements'
                  ? 'bg-yellow-400 text-gray-900'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              Live Corporate Announcements
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          {currentPlans.length > 0 ? (
            <div className={`grid grid-cols-1 gap-8 max-w-7xl mx-auto ${
              currentPlans.length === 1 ? 'md:grid-cols-1 max-w-md' :
              currentPlans.length === 2 ? 'md:grid-cols-2 max-w-4xl' :
              currentPlans.length === 3 ? 'md:grid-cols-3' :
              'md:grid-cols-4'
            }`}>
              {currentPlans.map((plan) => renderPlanCard(plan, plan.id === mostPopularId))}
            </div>
          ) : (
            <div className="max-w-4xl mx-auto text-center">
              <div className="bg-gray-50 p-12 rounded-lg">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">
                  {activeTab === 'stock_recommendations' 
                    ? 'Stock Recommendations Plans' 
                    : 'Live Corporate Announcements Plans'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {activeTab === 'stock_recommendations' 
                    ? 'No stock recommendation plans are currently available. Please check back later.'
                    : 'Stay updated with real-time corporate announcements and important market updates. Plans coming soon!'}
                </p>
                {activeTab === 'live_corporate_announcements' && (
                  <Link
                    to="/corporate-announcement"
                    className="inline-block bg-yellow-400 text-gray-900 px-8 py-3 rounded-full font-bold hover:bg-yellow-500 transition-colors"
                  >
                    View Announcements
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>

        {/* How It Works Video - only under Live Corporate Announcements tab */}
        {activeTab === 'live_corporate_announcements' && (
        <div className="container mx-auto px-6 pt-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">See How It Works</h2>
            <p className="text-gray-600 mb-8">
              Watch this short walkthrough to learn how Climb Capital helps you stay ahead with
              research-based recommendations and LIVE corporate announcements.
            </p>
            <div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
              <video
                src={climbVideo}
                controls
                playsInline
                preload="metadata"
                className="w-full h-auto"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
        )}

        {/* Corporate Announcement Banner */}
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-4xl mx-auto bg-blue-50 rounded-xl p-8">
            <p className="text-xl text-center text-gray-900 mb-6">
              To Get Important Corporate Announcement Daily End of Day (<span className="text-blue-600">Exclusively Free</span>) [For Registered Client]
            </p>
            <div className="flex justify-center">
              <a
                href="https://forms.gle/S66xLihj29dETJrv8"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-yellow-400 text-gray-900 px-8 py-3 rounded-full font-semibold hover:bg-yellow-500 transition-colors flex items-center gap-2"
              >
                <LinkIcon size={18} />
                Avail Service
              </a>
            </div>
          </div>
        </div>

        {/* Fee Structure Section */}
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-5xl mx-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-3">FEE STRUCTURE</h3>
            <p className="text-gray-600 leading-relaxed">
              We have intentionally set our fees at a nominal level to ensure accessibility for individual investors with smaller capital bases. Importantly, we do not engage in profit sharing. We recommend a minimum investment of ₹5 lakhs to facilitate portfolio diversification, although a higher investment is encouraged for even greater opportunities.
            </p>
          </div>
        </div>

      </section>

      {/* Sample Report Section */}
      <SampleReportSection />

      {/* Payment Checkout Modal */}
      <PaymentCheckoutModal
        isOpen={isCheckoutOpen}
        onClose={handleCloseCheckout}
        plan={selectedPlan}
        initialCouponCode={initialCoupon}
        couponTargetPlanId={couponTargetPlanId}
      />
    </div>
  );
};

export default Pricing;
